import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { setCookie, getCookie } from '../utils/cookieUtils';
import {
  clearCategory,
  isStorageAllowed,
  readStoredItem,
  subscribeToConsentChange,
  writeStoredItem,
} from '../utils/consentStorage';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

// Older visits may still have the mode in localStorage from before it needed
// consent; reading it back is harmless, and declining clears it.
const readSavedMode = (): ThemeMode | null => {
  const saved = getCookie('themeMode') || readStoredItem('themeMode');
  return saved === 'light' || saved === 'dark' ? saved : null;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>('dark');

  useEffect(() => {
    const savedMode = readSavedMode();
    if (savedMode) {
      setModeState(savedMode);
    }

    // Follow the system as long as the visitor has not picked a mode themselves.
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (!readSavedMode()) {
        setModeState(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // The mode is only ever written to the device once the visitor has agreed to
  // it, and withdrawing that agreement takes the earlier entry with it. Re-runs
  // on every consent change, so switching the theme setting off in the cookie
  // settings cleans up immediately instead of at the next theme toggle.
  useEffect(() => {
    const applyConsent = () => {
      if (isStorageAllowed('theme')) {
        writeStoredItem('theme', 'themeMode', mode);
        setCookie('themeMode', mode, 365);
      } else {
        clearCategory('theme');
      }
    };

    applyConsent();
    return subscribeToConsentChange(applyConsent);
  }, [mode]);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((current) => (current === 'light' ? 'dark' : 'light'));
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, toggleMode, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeMode = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};
